import React, { useState, useEffect } from 'react';
import { ProxySettingsData } from '../utils/types';
import { validProxySettings } from '../utils/validation';

const BASE_URL = process.env.REACT_APP_API_URL;
const token = process.env.REACT_APP_API_KEY;

const responseToJSON = (response: Response) => {
    if (response.ok) {
        return response.json();
    } else {
        throw new Error(
            `Error getting settings list: ${response.status} - ${response.statusText}`
        );
    }
}

/**
 * Hook used to get the list of settings
 * Returns an object with the following properties:
 * - settingsList: the list of settings
 * - loading: true if the list is being loaded
 * - error: error message if there is an error
 * - refresh: function to refresh the list
 */
export const useGetSettingsList = () => {
    const [settingsList, setSettingsList] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshFlag, setRefresh] = useState<boolean>(false);
    const refresh = () => setRefresh(!refreshFlag);
    const full_url = `${BASE_URL}/getsettings?code=${token}`;

    const updateSettingsList = (data: any) => {
        console.log(data);
        setSettingsList(data);
        setLoading(false);
        setError(null);
    };

    useEffect(() => {
        setLoading(true);
        fetch(full_url)
            .then(responseToJSON)
            .then(updateSettingsList)
            .catch(
                (error) => {
                    setError(error.message);
                });
    }, [refreshFlag]);

    return {
        settingsList,
        loading,
        error,
        refresh
    }
}

/**
 * Hook to delete settings
 * @returns deleteSettings function to delete settings
 * @returns loading true if the settings are being deleted
 * @returns error error message if there is an error
 */
export const useDeleteSettings = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const full_url = `${BASE_URL}/removeSettings?code=${token}`;

    /**
     * Delete settings, stores the error in the error state if there is one
     * @param settingNames names of the settings to delete
     */
    const deleteSettings = (settingNames: string[]) => {
        setLoading(true);
        fetch(full_url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ names: settingNames })
        })
            .then(responseToJSON)
            .then(() => {
                //response to JSON will check if the response is ok
                setLoading(false);
                setError(null);
            })
            .catch(() => {
                setError('Error deleting settings');
                setLoading(false);
            });
    }

    return {
        deleteSettings,
        loading,
        error
    }
}

/**
 * Hook to get a setting
 * @returns getSetting function to get a setting
 * @returns setting the setting data
 * @returns loading true if the setting is being loaded
 * @returns error error message if there is an error
 */
export const useGetSetting = () => {
    const [setting, setSetting] = useState<ProxySettingsData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * Get a setting and store it in the setting state
     * @param settingName name of the setting to get
     */
    const getSetting = (settingName: string) => {
        const requestBody = {
            name: settingName
        };
        const full_url = `${BASE_URL}/getsetting?code=${token}`;
        setLoading(true);
        fetch(full_url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
        })
            .then(responseToJSON)
            .then((data: any) => {
                const { b2cPolicy, displayName, method, endpoint, headers, restTriggers } = data;
                const settingData: ProxySettingsData = {
                    name: settingName,
                    displayName: displayName,
                    b2cPolicy: b2cPolicy,
                    method,
                    endpoint,
                    headers,
                    restTriggers: (restTriggers ? restTriggers : [])
                };
                setSetting(settingData);
                setLoading(false);
                setError(null);
            })
            .catch(() => {
                setError('Error getting setting');
                setLoading(false);
            });
    }

    return {
        getSetting,
        setting,
        loading,
        error
    };
}

/**
 * Hook to save a setting to database
 * @returns saveSetting function to save a setting
 * @returns loading true if the setting is being saved
 * @returns error error message if there is an error
 */
export const useSaveSetting = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * Save a setting
     * @param setting A list of settings to save
     */
    const saveSetting = (settings: ProxySettingsData[]) => {
        const full_url = `${BASE_URL}/uploadSettings?code=${token}`;
        const requestBody = {};
        console.log(settings);
        settings.forEach((setting) => {
            const { name, b2cPolicy, displayName, method, endpoint, headers } = setting;
            const settingData = {
                [name]: {
                    displayName: displayName,
                    b2cPolicy: b2cPolicy,
                    method: method,
                    endpoint: endpoint,
                    headers: headers
                }
            };
            Object.assign(
                requestBody,
                settingData
            );
            console.log(settingData);
            if (!validProxySettings(name, settingData[name])) {
                throw new Error('Invalid setting: ' + name)
            }
        });
        console.log(requestBody);
        setLoading(true);
        fetch(full_url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
        })
            .then(responseToJSON)
            .then(() => {
                setLoading(false);
                setError(null);
            })
            .catch((err) => {
                setError(err);
                setLoading(false);
            });
    }
    return {
        saveSetting,
        loading,
        error
    }
}
